/* ════ CO₂ / TRAVEL BOARD ══════════════════════════════════ */
// A self-contained gantt for travel "trips", each carrying a CO₂ figure, with a
// total report on top. Mirrors the planner's DOM/CSS but runs on its own data
// (state.trips) and a simpler whole-day engine — the planner is untouched.
import { state } from './state.js';
import { $, $$ } from './dom.js';
import { MO, TRAVEL_MODES, TRAVEL_ZONES, estimateCo2, flightCo2, HAUL_KM, ZONE_HAUL, FLIGHT_FACTOR, CO2_EST } from './config.js';
import { addD, ddiff, fdate, pdate, isWE, isMon, isoWk } from './utils/date.js';
import { recalcDayW, updatePeriodLbl } from './gantt/index.js';
import { sb } from './supabase/client.js';
import { setSyncDot, logActivity, normTrip } from './supabase/data.js';
import { showToast } from './utils/toast.js';

const esc = s => String(s || '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const MODE = Object.fromEntries(TRAVEL_MODES.map(m => [m.v, m]));
const resName = id => state.resources.find(r => r.id === id)?.name || 'Unassigned';
export const co2fmt = n => (Math.round((Number(n) || 0) * 1000) / 1000).toLocaleString('en-GB', { maximumFractionDigits: 3 }) + ' t';

// Mode filter (show/hide flights, hotels, cars…). Null = all shown.
function ensureModes() { if (!state.co2Modes) state.co2Modes = new Set(TRAVEL_MODES.map(m => m.v)); }
const modeOn = m => { ensureModes(); return state.co2Modes.has(m || 'other'); };
function renderModeChips() {
  const box = $('co2-modes'); if (!box) return; ensureModes();
  box.innerHTML = '<span class="co2-modes-lbl">Show</span>' + TRAVEL_MODES.map(m =>
    `<button class="co2-mode-chip${state.co2Modes.has(m.v) ? ' on' : ''}" data-mode="${m.v}">${m.ic} ${m.l}</button>`).join('');
}

// Suppress the click that fires right after a real drag.
let suppress = 0;
const suppressClick = () => { suppress = Date.now(); };
const clickSuppressed = () => Date.now() - suppress < 250;

/* ── Timeline header (mirrors gantt/header.js into #co2-hdr) ── */
function co2Header(ti) {
  const days = Array.from({ length: state.viewDays }, (_, i) => addD(state.viewStart, i));
  const months = [], weeks = []; let cm = null, cs = 0, cw = null, cws = 0;
  days.forEach(d => {
    const m = d.getMonth(), w = isoWk(d);
    if (m !== cm) { if (cm !== null) months.push({ m: cm, s: cs }); cm = m; cs = 1; } else cs++;
    if (w !== cw) { if (cw !== null) weeks.push({ w: cw, s: cws }); cw = w; cws = 1; } else cws++;
  });
  months.push({ m: cm, s: cs }); weeks.push({ w: cw, s: cws });
  const W = state.DAY_W;
  $('co2-hdr').innerHTML = `
    <div class="tl-months">${months.map(({ m, s }) => `<div class="mo-l" style="width:${s * W}px">${MO[m]}</div>`).join('')}</div>
    <div class="tl-weeks">${weeks.map(({ w, s }) => `<div class="wk-l" style="width:${s * W}px">W${w}</div>`).join('')}</div>
    <div class="tl-days">${days.map((d, i) => `<div class="dy-c${isWE(d) ? ' we' : ''}${isMon(d) ? ' mon' : ''}${i === ti ? ' tod' : ''}" style="width:${W}px">${d.getDate()}</div>`).join('')}</div>`;
}

// Greedy whole-day lane packing for one person's trips.
function packDays(list) {
  const idx = t => [ddiff(state.viewStart, pdate(t.start)), ddiff(state.viewStart, pdate(t.end))];
  const s = [...list].sort((a, b) => a.start.localeCompare(b.start));
  const lanes = [];
  s.forEach(t => {
    const [si, ei] = idx(t); let ok = false;
    for (const l of lanes) {
      if (!l.some(x => { const [xs, xe] = idx(x); return si <= xe && ei >= xs; })) { l.push(t); ok = true; break; }
    }
    if (!ok) lanes.push([t]);
  });
  return lanes;
}

/* ── Render ──────────────────────────────────────────────── */
export function renderCo2() {
  if (!$('co2-body')) return;
  recalcDayW(); updatePeriodLbl();
  const { viewDays, DAY_W, LANE_H } = state;
  const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
  const ti = ddiff(state.viewStart, todayMid);
  co2Header(ti);
  $('co2-inner').style.width = `${viewDays * DAY_W}px`;

  const sb2 = $('co2-sb'), body = $('co2-body');
  sb2.innerHTML = ''; body.innerHTML = '';
  state.resources.forEach((res, ri) => {
    const alt = ri % 2 === 1 ? ' alt' : '';
    const rt = state.trips.filter(t => t.resource === res.id && modeOn(t.mode));
    const lanes = packDays(rt), numL = Math.max(1, lanes.length), secH = numL * LANE_H;

    const nc = document.createElement('div'); nc.className = 'res-cell' + alt; nc.style.height = `${secH}px`;
    const tot = rt.reduce((s, t) => s + (Number(t.co2) || 0), 0);
    nc.innerHTML = `<div class="res-av" style="background:${res.col}22;color:${res.col}">${res.ini}</div>`
      + `<div class="res-meta"><span class="res-nm">${res.name}</span>${tot ? `<span class="res-role-lbl">${co2fmt(tot)}</span>` : ''}</div>`;
    sb2.appendChild(nc);

    const sec = document.createElement('div'); sec.className = 'res-sec' + alt; sec.dataset.resource = res.id;
    for (let li = 0; li < numL; li++) {
      const row = document.createElement('div'); row.className = 'lane-row'; row.style.height = `${LANE_H}px`;
      for (let i = 0; i < viewDays; i++) {
        const d = addD(state.viewStart, i);
        const bg = document.createElement('div');
        bg.className = 'dy-bg' + (isWE(d) ? ' we' : '') + (isMon(d) ? ' mon' : '') + (i === ti ? ' tod' : '');
        bg.style.width = `${DAY_W}px`; row.appendChild(bg);
      }
      sec.appendChild(row);
    }
    if (ti >= 0 && ti < viewDays) { const tl = document.createElement('div'); tl.className = 'today-line'; tl.style.left = `${ti * DAY_W + DAY_W / 2}px`; sec.appendChild(tl); }
    lanes.forEach((lane, li) => lane.forEach(t => renderTrip(t, li, sec)));
    const ghost = document.createElement('div'); ghost.className = 'draw-ghost co2g'; ghost.id = `cg-${res.id}`; sec.appendChild(ghost);
    setupDraw(sec, res.id);
    body.appendChild(sec);
  });
  populateControls();
  renderModeChips();
  renderReport();
  renderGraph();
}

/* ── Target + year controls ──────────────────────────────── */
function populateControls() {
  const ti = $('co2-target');
  if (ti && document.activeElement !== ti) ti.value = state.settings.co2_target ?? '';
  const ys = $('co2-year'); if (!ys) return;
  const years = [...new Set(state.trips.map(t => pdate(t.start).getFullYear()))];
  const now = new Date().getFullYear();
  [now, state.co2Year].forEach(y => { if (!years.includes(y)) years.push(y); });
  years.sort((a, b) => b - a);
  ys.innerHTML = years.map(y => `<option value="${y}"${y === state.co2Year ? ' selected' : ''}>${y}</option>`).join('');
}
async function saveSetting(key, value) {
  state.settings[key] = value;
  setSyncDot('busy');
  const { error } = await sb.from('app_settings').upsert({ key, value, updated_at: new Date().toISOString() });
  if (error) { setSyncDot('err'); showToast('Save failed: ' + error.message); } else { setSyncDot('ok'); showToast('Target saved ✓'); }
  renderGraph();
}

/* ── Monthly CO₂ vs target graph (inline SVG) ────────────── */
function renderGraph() {
  const box = $('co2-graph'); if (!box) return;
  const year = state.co2Year;
  const monthly = Array(12).fill(0);
  state.trips.forEach(t => { if (!modeOn(t.mode)) return; const d = pdate(t.start); if (d.getFullYear() === year) monthly[d.getMonth()] += Number(t.co2) || 0; });
  const target = parseFloat(state.settings.co2_target) || 0;
  const budget = target ? target / 12 : 0;
  const ytd = monthly.reduce((a, b) => a + b, 0);
  const max = Math.max(...monthly, budget, 0.001) * 1.2;
  const W = 720, H = 160, padL = 36, padR = 12, padT = 14, padB = 22;
  const pw = W - padL - padR, ph = H - padT - padB, base = padT + ph, slot = pw / 12, bw = Math.min(38, slot - 12);
  const y = v => base - (v / max) * ph;
  const MOI = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
  const bars = monthly.map((v, i) => {
    const x = padL + i * slot + (slot - bw) / 2, over = budget && v > budget;
    return `<rect x="${x.toFixed(1)}" y="${y(v).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, base - y(v)).toFixed(1)}" rx="3" fill="${over ? 'var(--red)' : 'var(--out)'}"><title>${MOI[i]}: ${co2fmt(v)}</title></rect>`
      + `<text x="${(x + bw / 2).toFixed(1)}" y="${H - 7}" text-anchor="middle" class="cg-mo">${MOI[i]}</text>`;
  }).join('');
  const budgetLine = budget ? `<line x1="${padL}" y1="${y(budget).toFixed(1)}" x2="${W - padR}" y2="${y(budget).toFixed(1)}" class="cg-budget"/><text x="${W - padR}" y="${(y(budget) - 5).toFixed(1)}" text-anchor="end" class="cg-bl">monthly budget ${co2fmt(budget)}</text>` : '';
  const pct = target ? Math.round(ytd / target * 100) : 0;
  const head = `<div class="cg-head"><span class="cg-yr">${year}</span><b>${co2fmt(ytd)}</b>`
    + (target ? `<span class="cg-of">of ${co2fmt(target)} target</span><span class="cg-pct${pct > 100 ? ' over' : ''}">${pct}%</span>` : `<span class="cg-of">no target set — enter one above</span>`)
    + `</div>`;
  box.innerHTML = head + `<svg viewBox="0 0 ${W} ${H}" class="cg-svg" role="img" aria-label="Monthly CO₂ for ${year}">
    <line x1="${padL}" y1="${base}" x2="${W - padR}" y2="${base}" class="cg-axis"/>
    <text x="${padL - 7}" y="${padT + 5}" text-anchor="end" class="cg-y">${Math.round(max * 10) / 10}</text>
    <text x="${padL - 7}" y="${base + 3}" text-anchor="end" class="cg-y">0</text>
    ${bars}${budgetLine}</svg>`;
}

function renderTrip(t, li, sec) {
  const { viewDays, DAY_W, LANE_H } = state;
  const s = ddiff(state.viewStart, pdate(t.start)), e = ddiff(state.viewStart, pdate(t.end));
  if (e < 0 || s >= viewDays) return;
  const cs = Math.max(0, s), ce = Math.min(viewDays - 1, e);
  const w = Math.max((ce - cs + 1) * DAY_W - 3, 18);
  const el = document.createElement('div');
  el.id = `ctb-${t.id}`;
  el.className = `trip-block mode-${t.mode || 'other'}`;
  el.style.cssText = `left:${cs * DAY_W + 1}px;top:${li * LANE_H + 7}px;width:${w}px;height:${LANE_H - 14}px`;
  el.innerHTML = `<span class="trip-ic">${MODE[t.mode]?.ic || '•'}</span>`
    + `<span class="trip-nm">${esc(t.name || resName(t.resource))}</span>`
    + `${t.co2 ? `<span class="trip-co2">${co2fmt(t.co2)}</span>` : ''}`;
  el.title = `${t.name || ''}${t.client ? ' · ' + t.client : ''}\n${MODE[t.mode]?.l || ''} · ${t.zone || ''} · ${co2fmt(t.co2)}\n${t.start} → ${t.end}`;
  el.addEventListener('click', ev => { ev.stopPropagation(); if (clickSuppressed()) return; openTripEdit(t.id); });
  el.addEventListener('mousedown', ev => { ev.stopPropagation(); startMove(ev, t.id); });
  sec.appendChild(el);
}

/* ── Draw to create ──────────────────────────────────────── */
function setupDraw(sec, resId) {
  sec.addEventListener('mousedown', e => {
    if (e.target.closest('.trip-block')) return;
    e.preventDefault();
    const ts = $('co2-scroll'), ghost = $(`cg-${resId}`);
    const dayAt = cx => Math.max(0, Math.min(state.viewDays - 1, Math.floor((cx - ts.getBoundingClientRect().left + ts.scrollLeft) / state.DAY_W)));
    const start = dayAt(e.clientX); let cur = start;
    const draw = () => {
      const a = Math.min(start, cur), b = Math.max(start, cur);
      ghost.style.cssText = `display:block;left:${a * state.DAY_W}px;top:7px;width:${(b - a + 1) * state.DAY_W - 2}px;height:${state.LANE_H - 14}px`;
    };
    draw();
    const onMove = ev => { cur = dayAt(ev.clientX); draw(); };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp);
      ghost.style.display = 'none'; suppressClick();
      const a = Math.min(start, cur), b = Math.max(start, cur);
      openTripNew(fdate(addD(state.viewStart, a)), fdate(addD(state.viewStart, b)), resId);
    };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  });
}

/* ── Drag to move (whole days) ───────────────────────────── */
function startMove(e, id) {
  const t = state.trips.find(x => x.id === id); if (!t) return;
  const el = $(`ctb-${id}`), startX = e.clientX; let delta = 0, moved = false;
  const onMove = ev => { delta = Math.round((ev.clientX - startX) / state.DAY_W); if (Math.abs(ev.clientX - startX) > 4) moved = true; el.style.transform = `translateX(${delta * state.DAY_W}px)`; };
  const onUp = () => {
    document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp);
    if (!moved || !delta) { el.style.transform = ''; return; }
    suppressClick();
    tripUpdate(id, { start: fdate(addD(pdate(t.start), delta)), end: fdate(addD(pdate(t.end), delta)) });
  };
  document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
}

/* ── Total report ────────────────────────────────────────── */
function tripsInRange() {
  const f = $('co2-from').value, to = $('co2-to').value;
  return state.trips.filter(t => modeOn(t.mode) && !(f && t.end < f) && !(to && t.start > to));
}
function renderReport() {
  const box = $('co2-totals'); if (!box) return;
  const list = tripsInRange();
  const total = list.reduce((s, t) => s + (Number(t.co2) || 0), 0);
  const sum = keyFn => { const m = new Map(); list.forEach(t => m.set(keyFn(t), (m.get(keyFn(t)) || 0) + (Number(t.co2) || 0))); return m; };
  const byMode = sum(t => t.mode || 'other'), byZone = sum(t => t.zone || 'UK');
  const modeChips = TRAVEL_MODES.filter(m => byMode.get(m.v)).map(m =>
    `<span class="co2-chip">${m.ic} ${m.l}<b>${co2fmt(byMode.get(m.v))}</b></span>`).join('');
  const zoneChips = TRAVEL_ZONES.filter(z => byZone.get(z)).map(z =>
    `<span class="co2-chip zone">${z}<b>${co2fmt(byZone.get(z))}</b></span>`).join('');
  box.innerHTML = `
    <div class="co2-headline"><div class="co2-big">${co2fmt(total)}</div><div class="co2-sub">${list.length} trip${list.length === 1 ? '' : 's'} · total CO₂e</div></div>
    <div class="co2-chips">${modeChips || '<span class="co2-empty">No trips in range</span>'}${zoneChips}</div>`;
}

function exportCsv() {
  const list = tripsInRange().slice().sort((a, b) => a.start.localeCompare(b.start));
  if (!list.length) { showToast('Nothing to export'); return; }
  const rows = [['Person', 'Trip', 'Client', 'Mode', 'Zone', 'Start', 'End', 'CO2 (t)']];
  list.forEach(t => rows.push([resName(t.resource), t.name, t.client || '', t.mode || '', t.zone || '', t.start, t.end, Number(t.co2) || 0]));
  rows.push(['', '', '', '', '', '', 'TOTAL', list.reduce((s, t) => s + (Number(t.co2) || 0), 0)]);
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
  a.download = `co2-trips-${fdate(new Date())}.csv`; a.click();
  showToast('CO₂ report exported ✓');
}

/* ── Trip modal ──────────────────────────────────────────── */
function fillRes(sel) {
  $('trip-res').innerHTML = state.resources.map(r => `<option value="${r.id}"${r.id === sel ? ' selected' : ''}>${esc(r.name)}</option>`).join('');
}
function fillSelects() {
  $('trip-mode').innerHTML = TRAVEL_MODES.map(m => `<option value="${m.v}">${m.ic} ${m.l}</option>`).join('');
  $('trip-zone').innerHTML = TRAVEL_ZONES.map(z => `<option value="${z}">${z}</option>`).join('');
}
function nightsBetween() {
  const s = $('trip-start').value, e = $('trip-end').value;
  return s && e ? Math.max(1, ddiff(pdate(s), pdate(e))) : 1;
}
const isFlight = () => $('trip-mode').value === 'flight';
const defaultKm = zone => HAUL_KM[ZONE_HAUL[zone]] || HAUL_KM.short;
function syncDist() { $('trip-dist-fld').style.display = isFlight() ? '' : 'none'; }
function autoEstimate() {
  const mode = $('trip-mode').value, zone = $('trip-zone').value, ret = $('trip-return').checked;
  if (mode === 'flight') {
    const km = parseFloat($('trip-dist').value) || defaultKm(zone);
    $('trip-co2').value = flightCo2(zone, km, ret);
  } else {
    $('trip-co2').value = estimateCo2(mode, zone, nightsBetween(), ret);
  }
}
export function openTripNew(start, end, resId) {
  state.editTripId = null;
  $('trip-m-title').textContent = 'New trip';
  $('trip-del').style.display = 'none';
  $('trip-name').value = ''; $('trip-client').value = ''; $('trip-job').value = ''; $('trip-info').value = '';
  $('trip-start').value = start || ''; $('trip-end').value = end || start || '';
  $('trip-return').checked = false;
  fillSelects(); $('trip-mode').value = 'flight'; $('trip-zone').value = 'UK';
  fillRes(resId || state.resources[1]?.id || 'unassigned');
  $('trip-dist').value = defaultKm('UK'); delete $('trip-dist').dataset.touched;
  syncDist();
  delete $('trip-co2').dataset.touched;   // fresh trip → track the auto-estimate
  autoEstimate();
  $('trip-modal').classList.add('open');
  setTimeout(() => $('trip-name').focus(), 200);
}
export function openTripEdit(id) {
  const t = state.trips.find(x => x.id === id); if (!t) return;
  state.editTripId = id;
  $('trip-m-title').textContent = 'Edit trip';
  $('trip-del').style.display = 'inline-flex';
  fillSelects();
  $('trip-name').value = t.name || ''; $('trip-client').value = t.client || ''; $('trip-job').value = t.job || '';
  $('trip-info').value = t.info || ''; $('trip-start').value = t.start; $('trip-end').value = t.end;
  $('trip-mode').value = t.mode || 'other'; $('trip-zone').value = t.zone || 'UK'; $('trip-return').checked = false;
  $('trip-dist').value = t.distance_km ?? (t.mode === 'flight' ? defaultKm(t.zone) : '');
  if (t.distance_km) $('trip-dist').dataset.touched = '1'; else delete $('trip-dist').dataset.touched;
  syncDist();
  $('trip-co2').value = t.co2 ?? '';
  $('trip-co2').dataset.touched = '1';   // keep the saved figure unless re-estimated
  fillRes(t.resource);
  $('trip-modal').classList.add('open');
}
function closeTrip() { $('trip-modal').classList.remove('open'); state.editTripId = null; }

async function saveTrip() {
  const start = $('trip-start').value, end = $('trip-end').value;
  if (!start || !end) { showToast('Pick start and end dates'); return; }
  if (end < start) { showToast('End date is before the start'); return; }
  const data = {
    name: $('trip-name').value.trim(), resource: $('trip-res').value,
    start, end, mode: $('trip-mode').value, zone: $('trip-zone').value,
    co2: parseFloat($('trip-co2').value) || 0,
    distance_km: isFlight() ? (parseFloat($('trip-dist').value) || null) : null,
    client: $('trip-client').value.trim(), job: $('trip-job').value.trim(), info: $('trip-info').value.trim(),
  };
  setSyncDot('busy'); let error, rows;
  if (state.editTripId) {
    ({ data: rows, error } = await sb.from('trips').update(data).eq('id', state.editTripId).select());
    if (!error && rows?.[0]) state.trips = state.trips.map(t => t.id === state.editTripId ? normTrip(rows[0]) : t);
  } else {
    data.created_by = state.currentUser?.email || null;
    ({ data: rows, error } = await sb.from('trips').insert(data).select());
    if (!error && rows?.[0] && !state.trips.find(t => t.id === rows[0].id)) state.trips.push(normTrip(rows[0]));
  }
  if (error) { setSyncDot('err'); showToast('Save failed: ' + error.message); return; }
  setSyncDot('ok'); showToast(state.editTripId ? 'Trip updated ✓' : 'Trip added ✓');
  logActivity(state.editTripId ? 'updated' : 'added', 'trip', data.name || resName(data.resource));
  closeTrip(); renderCo2();
}
async function tripUpdate(id, changes) {
  const t = state.trips.find(x => x.id === id); if (t) Object.assign(t, changes);
  renderCo2();   // optimistic
  setSyncDot('busy');
  const { error } = await sb.from('trips').update(changes).eq('id', id);
  if (error) { setSyncDot('err'); showToast('Sync error: ' + error.message); } else setSyncDot('ok');
}
async function deleteTrip() {
  if (!state.editTripId || !confirm('Delete this trip?')) return;
  const id = state.editTripId, t = state.trips.find(x => x.id === id), nm = t?.name || '';
  setSyncDot('busy'); const { error } = await sb.from('trips').delete().eq('id', id);
  if (error) { setSyncDot('err'); showToast('Delete failed: ' + error.message); return; }
  state.trips = state.trips.filter(x => x.id !== id);
  // Remember imported events so a re-import doesn't bring them back.
  if (t?.source_key) {
    state.tripIgnores.add(t.source_key);
    sb.from('trip_ignores').upsert({ source_key: t.source_key, subject: nm, ignored_by: state.currentUser?.email || null }).then(() => {});
  }
  setSyncDot('ok'); showToast('Trip deleted'); logActivity('deleted', 'trip', nm); closeTrip(); renderCo2();
}

// Recalculate CO₂ for imported flights using the current official factors.
// Scoped to imported flights (source_key present) so manual/return trips aren't
// clobbered — those keep their hand-set figure.
async function recalcFlights() {
  const flights = state.trips.filter(t => t.mode === 'flight' && t.source_key);
  if (!flights.length) { showToast('No imported flights to recalculate'); return; }
  if (!confirm(`Recalculate CO₂ for ${flights.length} imported flight(s) using the official business-class factors? This overwrites their current CO₂ figure.`)) return;
  setSyncDot('busy');
  let n = 0, err = null;
  for (const t of flights) {
    const dist = Number(t.distance_km) || defaultKm(t.zone);
    const co2 = flightCo2(t.zone, dist, false);
    if (co2 !== Number(t.co2) || dist !== Number(t.distance_km)) {
      ({ error: err } = await sb.from('trips').update({ co2, distance_km: dist }).eq('id', t.id));
      if (err) break;
      t.co2 = co2; t.distance_km = dist; n++;
    }
  }
  if (err) { setSyncDot('err'); showToast('Recalc failed: ' + err.message); return; }
  setSyncDot('ok'); showToast(n ? `Recalculated ${n} flight(s) ✓` : 'All flights already up to date'); renderCo2();
}

// Build the "how it's calculated" table straight from the constants in config,
// so what's shown always matches what the app actually computes.
function openMethod() {
  const HAUL_LBL = { domestic: 'Domestic', short: 'Short-haul', long: 'Long-haul' };
  const flightRows = TRAVEL_ZONES.map(z => {
    const h = ZONE_HAUL[z];
    return `<tr><td><b>${z}</b></td><td>${HAUL_LBL[h]}</td><td class="num">${FLIGHT_FACTOR[h]}</td><td class="num">${HAUL_KM[h].toLocaleString()}</td></tr>`;
  }).join('');
  const otherRows = ['train', 'car', 'hotel'].map(m => {
    const lbl = TRAVEL_MODES.find(x => x.v === m);
    const per = m === 'hotel' ? 'per night' : 'per leg';
    return `<tr><td>${lbl.ic} ${lbl.l}</td><td class="num">${CO2_EST[m].UK}</td><td class="num">${CO2_EST[m].EU}</td><td class="num">${CO2_EST[m].US}</td><td>t, ${per}</td></tr>`;
  }).join('');
  const eg = flightCo2('EU', 1500, true);
  $('co2-method-body').innerHTML = `
    <h3>Flights — official factors</h3>
    <p>UK Government <b>2026 GHG conversion factors</b> (Business travel – air), <b>business class</b>, <b>to/from&nbsp;UK</b>, including radiative forcing. Units: <b>kg CO₂e per passenger·km</b>.</p>
    <p class="co2-formula">CO₂ (tonnes) = distance (km, each way) × factor × (return ? 2 : 1) ÷ 1000</p>
    <table class="co2-mtable">
      <thead><tr><th>Zone</th><th>Haul (to/from UK)</th><th>Factor</th><th>Default km*</th></tr></thead>
      <tbody>${flightRows}</tbody>
    </table>
    <p class="co2-eg">Example: EU short-haul <b>return</b>, 1,500&nbsp;km each way → 1,500 × ${FLIGHT_FACTOR.short} × 2 ÷ 1000 = <b>${eg} t</b>.</p>
    <p class="co2-fine">*Used only when a flight has no distance entered — type the real km on the trip for an exact figure.</p>
    <h3>Other travel — indicative</h3>
    <p>Not official per-km factors — simple estimates for logging (flights are the accurate part). Tonnes CO₂e:</p>
    <table class="co2-mtable">
      <thead><tr><th>Mode</th><th>UK</th><th>EU</th><th>US</th><th>Basis</th></tr></thead>
      <tbody>${otherRows}</tbody>
    </table>
    <p class="co2-src">Source: UK Government Greenhouse Gas Conversion Factors 2026, full set — “Business travel – air”. Zones map to UK-relative haul: UK→domestic, EU→short-haul, US→long-haul.</p>`;
  $('co2-method-modal').classList.add('open');
}

export function initCo2() {
  if (!$('co2-body')) return;
  $('co2-recalc').addEventListener('click', recalcFlights);
  $('co2-method').addEventListener('click', openMethod);
  $('co2-method-x').addEventListener('click', () => $('co2-method-modal').classList.remove('open'));
  $('co2-method-modal').addEventListener('click', e => { if (e.target.id === 'co2-method-modal') $('co2-method-modal').classList.remove('open'); });
  $('trip-add').addEventListener('click', () => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    openTripNew(fdate(t), fdate(t), state.resources[1]?.id || 'unassigned');
  });
  $('co2-export').addEventListener('click', exportCsv);
  $('co2-modes').addEventListener('click', e => {
    const b = e.target.closest('.co2-mode-chip'); if (!b) return; ensureModes();
    const m = b.dataset.mode;
    if (state.co2Modes.has(m)) state.co2Modes.delete(m); else state.co2Modes.add(m);
    renderCo2();
  });
  $('co2-target').addEventListener('change', e => saveSetting('co2_target', e.target.value.trim()));
  $('co2-year').addEventListener('change', e => { state.co2Year = +e.target.value; renderGraph(); });
  ['co2-from', 'co2-to'].forEach(id => $(id).addEventListener('change', renderReport));
  $('trip-save').addEventListener('click', saveTrip);
  $('trip-del').addEventListener('click', deleteTrip);
  $('trip-x').addEventListener('click', closeTrip);
  $('trip-cancel').addEventListener('click', closeTrip);
  $('trip-est').addEventListener('click', () => { delete $('trip-co2').dataset.touched; autoEstimate(); });
  $('trip-mode').addEventListener('change', () => {
    syncDist();
    if (isFlight() && !$('trip-dist').dataset.touched) $('trip-dist').value = defaultKm($('trip-zone').value);
    if (!$('trip-co2').dataset.touched) autoEstimate();
  });
  $('trip-zone').addEventListener('change', () => {
    if (isFlight() && !$('trip-dist').dataset.touched) $('trip-dist').value = defaultKm($('trip-zone').value);
    if (!$('trip-co2').dataset.touched) autoEstimate();
  });
  ['trip-return', 'trip-start', 'trip-end'].forEach(id => $(id).addEventListener('change', () => {
    if (!$('trip-co2').dataset.touched) autoEstimate();
  }));
  $('trip-dist').addEventListener('input', () => { $('trip-dist').dataset.touched = '1'; if (!$('trip-co2').dataset.touched) autoEstimate(); });
  $('trip-co2').addEventListener('input', () => { $('trip-co2').dataset.touched = '1'; });
  $('trip-modal').addEventListener('click', e => { if (e.target.id === 'trip-modal') closeTrip(); });
}
